import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usps } from '../data';
import ReviewSlider from '../components/ReviewSlider';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [serviceIndex, setServiceIndex] = useState(0);
  const [visiblePanels, setVisiblePanels] = useState(4);
  const [animateForm, setAnimateForm] = useState(false);

  const [heroFormData, setHeroFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  });

  const [heroStatus, setHeroStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleHeroChange = (e) => {
    const { name, value } = e.target;
    setHeroFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();

    if (!heroFormData.navn || !heroFormData.telefon || !heroFormData.email || !heroFormData.beskrivelse) {
      setHeroStatus({ submitting: false, success: false, error: 'Udfyld venligst alle påkrævede felter.' });
      return;
    }

    setHeroStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(heroFormData)
      });

      if (response.ok) {
        setHeroStatus({ submitting: false, success: true, error: null });
        setHeroFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setHeroStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der opstod en fejl. Prøv venligst igen.'
        });
      }
    } catch (err) {
      setHeroStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Prøv venligst igen.'
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, projectsRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/ydelser'),
          fetch('https://api.dev.auxo.dk/public/content/projekter')
        ]);

        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(servicesData);
        }
        if (projectsRes.ok) {
          const projectsData = await projectsRes.json();
          setProjects(projectsData.slice(0, 3)); // Show top 3 projects on home
        }
      } catch (err) {
        console.error('Error fetching CMS content:', err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setVisiblePanels(4);
      } else if (window.innerWidth >= 768) {
        setVisiblePanels(2);
      } else {
        setVisiblePanels(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Trigger entry animation for the webform
    const timer = setTimeout(() => setAnimateForm(true), 150);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const nextService = () => {
    if (services.length === 0) return;
    const maxIndex = services.length - visiblePanels;
    setServiceIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevService = () => {
    if (services.length === 0) return;
    const maxIndex = services.length - visiblePanels;
    setServiceIndex((prev) => (prev <= 0 ? Math.max(0, maxIndex) : prev - 1));
  };

  return (
    <>
      <Helmet>
        <title>Tømrer og maler på Djursland og i Ebeltoft - Få et tilbud i dag</title>
        <meta name="description" content="Søger du en professionel tømrer eller maler i Djursland og omegn? Vi tilbyder alt inden for tømrer- og malerfaget. Kontakt os i dag." />
        <meta property="og:title" content="Tømrer og maler på Djursland og i Ebeltoft - Få et tilbud i dag" />
        <meta property="og:description" content="Søger du en professionel tømrer eller maler i Djursland og omegn? Vi tilbyder alt inden for tømrer- og malerfaget. Kontakt os i dag." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/img-0027-scaled.jpg" />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-slate-900 text-white">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/img-0027-scaled.jpg"
            alt="Professionelt tømrer- og malerarbejde på Djursland - Tømrerfirmaet RNS"
            className="w-full h-full object-cover object-center opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/65 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight uppercase tracking-wider">
              Tømrer & Maler på Djursland
            </h1>
            <p className="text-lg max-w-2xl leading-relaxed" style={{ color: '#f1f1f1' }}>
              Erfarent tømrerfirma med base i Glatved. Vi leverer solidt håndværk på hele Djursland – uanset om det gælder nybyggeri, renovering, tagarbejde eller maleropgaver.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/kontakt"
                className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all shadow-md flex items-center gap-2"
              >
                Få et uforpligtende tilbud
                <i className="fa-solid fa-arrow-right" />
              </Link>
              <Link
                to="/referencer"
                className="bg-white hover:bg-slate-100 text-slate-900 font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all border border-white shadow-md"
              >
                Se vores projekter
              </Link>
            </div>
          </div>

          {/* Side Card / Webform */}
          <div className="lg:col-span-5 w-full relative z-20">
            <div className={`bg-slate-950 p-6 sm:p-8 border-b border-white shadow-2xl space-y-6 rounded-none text-white relative overflow-hidden transition-all duration-1000 ease-out ${
              animateForm 
                ? 'opacity-100 translate-y-0 lg:translate-y-36' 
                : 'opacity-0 translate-y-12 lg:translate-y-48'
            }`}>
              {/* Webform Watermark Logo */}
              <div className="absolute -right-12 -bottom-12 opacity-[0.04] pointer-events-none select-none z-0">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/logo_1.png"
                  alt=""
                  aria-hidden="true"
                  className="w-64 h-64 object-contain"
                />
              </div>

              <div className="relative z-10 space-y-6">
                <div className="space-y-3">
                  <div className="mb-2">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/logo_1.png"
                      alt="Tømrerfirmaet RNS logo - Få et uforpligtende tilbud på Djursland"
                      className="h-12 md:h-16 object-contain select-none pointer-events-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-display font-bold text-xl text-white uppercase tracking-wider">
                      Få et uforpligtende tilbud
                    </h3>
                    <p className="text-xs text-[#f1f1f1]">
                      Udfyld formularen, så vender vi tilbage til dig hurtigst muligt.
                    </p>
                  </div>
                </div>

              {heroStatus.success ? (
                <div className="bg-emerald-950/50 border border-emerald-800 text-emerald-200 p-4 rounded-none text-sm flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-lg text-emerald-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold mb-1">Tak for din henvendelse!</h4>
                    <p className="text-xs text-emerald-300">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleHeroSubmit} className="space-y-4">
                  {/* Honeypot field - absolutely hidden from humans */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                    <label htmlFor="hero_website_url">Do not fill this field</label>
                    <input
                      id="hero_website_url"
                      type="text"
                      name="website_url"
                      value={heroFormData.website_url}
                      onChange={handleHeroChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="hero_navn" className="block text-xs font-semibold text-[#f1f1f1] mb-1 uppercase tracking-wider">
                      Navn <span className="text-brand-primary">*</span>
                    </label>
                    <input
                      id="hero_navn"
                      type="text"
                      name="navn"
                      required
                      value={heroFormData.navn}
                      onChange={handleHeroChange}
                      className="w-full px-3 py-2 bg-transparent border-0 border-b border-white text-white placeholder-white/60 focus:outline-none focus:border-brand-primary transition-all rounded-none text-sm"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="hero_telefon" className="block text-xs font-semibold text-[#f1f1f1] mb-1 uppercase tracking-wider">
                        Telefon <span className="text-brand-primary">*</span>
                      </label>
                      <input
                        id="hero_telefon"
                        type="tel"
                        name="telefon"
                        required
                        value={heroFormData.telefon}
                        onChange={handleHeroChange}
                        className="w-full px-3 py-2 bg-transparent border-0 border-b border-white text-white placeholder-white/60 focus:outline-none focus:border-brand-primary transition-all rounded-none text-sm"
                        placeholder="F.eks. 22 43 91 47"
                      />
                    </div>

                    <div>
                      <label htmlFor="hero_email" className="block text-xs font-semibold text-[#f1f1f1] mb-1 uppercase tracking-wider">
                        E-mail <span className="text-brand-primary">*</span>
                      </label>
                      <input
                        id="hero_email"
                        type="email"
                        name="email"
                        required
                        value={heroFormData.email}
                        onChange={handleHeroChange}
                        className="w-full px-3 py-2 bg-transparent border-0 border-b border-white text-white placeholder-white/60 focus:outline-none focus:border-brand-primary transition-all rounded-none text-sm"
                        placeholder="din@email.dk"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="hero_beskrivelse" className="block text-xs font-semibold text-[#f1f1f1] mb-1 uppercase tracking-wider">
                      Beskrivelse af opgaven <span className="text-brand-primary">*</span>
                    </label>
                    <textarea
                      id="hero_beskrivelse"
                      name="beskrivelse"
                      required
                      rows="3"
                      value={heroFormData.beskrivelse}
                      onChange={handleHeroChange}
                      className="w-full px-3 py-2 bg-transparent border-0 border-b border-white text-white placeholder-white/60 focus:outline-none focus:border-brand-primary transition-all rounded-none text-sm resize-none"
                      placeholder="Hvad skal du have lavet?"
                    />
                  </div>

                  {heroStatus.error && (
                    <p className="text-xs text-rose-600 font-medium">{heroStatus.error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={heroStatus.submitting}
                    className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-3 px-4 rounded-none transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 text-sm"
                  >
                    {heroStatus.submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" />
                        Sender...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send forespørgsel
                      </>
                    )}
                  </button>
                </form>
              )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USPs Section */}
      <section className="py-20 lg:pt-36 lg:pb-32 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 uppercase tracking-wider">
              Professionelt håndværk du kan stole på
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Vi leverer kvalitetsløsninger til både private, erhverv og boligforeninger. Vores arbejde tager altid udgangspunkt i dit behov.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            {/* Left Column: Solid brand color block with text */}
            <div className="lg:col-span-4 bg-brand-primary text-white p-12 flex flex-col justify-center items-center text-center relative overflow-hidden min-h-[320px] lg:min-h-full shadow-lg">
              {/* Decorative background pattern */}
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
              
              <div className="relative z-10 space-y-6">
                <h3 className="font-display font-extrabold text-2xl md:text-3xl uppercase tracking-wider leading-tight">
                  KVALITET & TROVÆRDIGHED
                </h3>
                <div className="w-12 h-[2px] bg-white mx-auto"></div>
                <p className="text-sm md:text-base text-white/90 leading-relaxed max-w-xs mx-auto">
                  Vi leverer solidt, traditionelt tømrer- og malerarbejde på hele Djursland. Din lokale samarbejdspartner, når kvaliteten skal være i top.
                </p>
              </div>
            </div>

            {/* Right Column: USPs in a 2-column grid */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
              {usps.slice(0, 4).map((usp, idx) => (
                <div 
                  key={idx} 
                  className="bg-white p-6 rounded-none border border-slate-100 shadow-sm space-y-3 hover:shadow-md transition-all flex flex-col justify-between h-full"
                >
                  <div>
                    <div className="w-10 h-10 rounded-none bg-brand-primary flex items-center justify-center text-white text-lg mb-3">
                      <i className={usp.icon} />
                    </div>
                    <h3 className="font-display font-bold text-base text-slate-900 uppercase tracking-wider">
                      {usp.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mt-2">
                      {usp.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO & Ydelser Content Section */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Main SEO Text Column */}
            <div className="lg:col-span-7 space-y-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200/60 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
                  <i className="fa-solid fa-hammer" aria-hidden="true" />
                  <span>Lokal tømrer & håndværker</span>
                </div>
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 uppercase tracking-wider leading-tight">
                  Tømrer på Djursland til små og store byggeopgaver
                </h2>
                <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                  Leder du efter en tømrer, som kan hjælpe med både den mindre opgave og det større byggeprojekt? Hos Tømrerfirmaet RNS udfører vi <Link to="/ydelser/toemrer" className="text-brand-primary font-semibold underline decoration-brand-primary/40 hover:decoration-brand-primary">tømrerarbejde</Link> for private, erhverv og boligforeninger. Vi har base i Glatved ved Balle og kører i Grenaa, Tirstrup, Ebeltoft og resten af Djursland.
                </p>
              </div>

              {/* Sub-block 1 */}
              <div className="bg-slate-50 border-l-4 border-brand-primary p-6 sm:p-8 space-y-4">
                <h3 className="font-display font-bold text-xl text-slate-900 uppercase tracking-wide flex items-center gap-3">
                  <i className="fa-solid fa-shapes text-brand-primary text-lg" aria-hidden="true" />
                  Et tømrerfirma med flere håndværksfag samlet
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Som tømrerfirma løser vi traditionelle <Link to="/ydelser/toemrer" className="text-brand-primary font-semibold hover:underline">tømreropgaver</Link> og udfører snedkerarbejde og <Link to="/ydelser/maler" className="text-brand-primary font-semibold hover:underline">malerarbejde</Link>. Det giver dig mulighed for at få flere dele af projektet samlet hos den samme virksomhed. Vi hjælper blandt andet med gulve, gipsvægge, hegn, <Link to="/ydelser/tilbygning" className="text-brand-primary font-semibold hover:underline">tilbygninger</Link>, nybyggeri og <Link to="/ydelser/ombygning" className="text-brand-primary font-semibold hover:underline">renovering</Link>.
                </p>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Nogle kunder skal have udskiftet en dør, mens andre har brug for en gennemgribende renovering af boligen, <Link to="/ydelser/sommerhusrenovering" className="text-brand-primary font-semibold hover:underline">sommerhuset</Link>, butikken eller <Link to="/ydelser/ombygning" className="text-brand-primary font-semibold hover:underline">kontoret</Link>. Vi tilpasser arbejdet til bygningen og dine ønsker, så du får en løsning, der hænger sammen med både funktion, udtryk og økonomi.
                </p>
              </div>

              {/* Sub-block 2 */}
              <div className="space-y-4">
                <h3 className="font-display font-bold text-xl text-slate-900 uppercase tracking-wide flex items-center gap-3">
                  <i className="fa-solid fa-compass-drafting text-brand-primary text-lg" aria-hidden="true" />
                  Tømrerarbejde tilpasset din bygning
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Godt tømrerarbejde begynder med en grundig afklaring af opgaven. Vi taler med dig om behov, materialer og praktiske muligheder, inden arbejdet bliver planlagt. Undervejs holder vi en åben dialog, så du ved, hvordan projektet skrider frem, og hvilke valg der skal træffes.
                </p>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Vi arbejder på både helårsboliger, <Link to="/ydelser/sommerhusrenovering" className="text-brand-primary font-semibold hover:underline">sommerhuse</Link>, erhvervsejendomme og bygninger til boligforeninger. Uanset bygningens type ser vi på den samlede løsning og de forhold, der har betydning for resultatet. Det kan være arkitektur, energikrav, indeklima, slid og behovet for løbende vedligeholdelse.
                </p>
              </div>

              {/* Sub-block 3 */}
              <div className="space-y-4">
                <h3 className="font-display font-bold text-xl text-slate-900 uppercase tracking-wide flex items-center gap-3">
                  <i className="fa-solid fa-window-frame-open text-brand-primary text-lg" aria-hidden="true" />
                  Få hjælp til vinduer, tag og renovering
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Trænger boligen til <Link to="/ydelser/udskiftning-af-vinduer" className="text-brand-primary font-semibold hover:underline">nye vinduer og døre</Link>, kan vi hjælpe med opmåling, rådgivning og montering. En korrekt udskiftning kan bidrage til mindre træk, bedre komfort og et lavere varmeforbrug. Vi sørger samtidig for en pæn afslutning omkring rammer, lysninger, gerigter og sålbænke.
                </p>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Vi udfører også tagarbejde, når det eksisterende tag skal have en <Link to="/ydelser/tagrenovering" className="text-brand-primary font-semibold hover:underline">tagrenovering</Link> eller komplet <Link to="/ydelser/asbestarbejde-og-tagrenovering" className="text-brand-primary font-semibold hover:underline">udskiftning af tag</Link>. Opgaven kan omfatte tagbelægning, undertag, lægter, isolering, tagrender og inddækninger. Før arbejdet går i gang, gennemgår vi de relevante dele af taget og rådgiver om en løsning, der passer til bygningen.
                </p>
              </div>

              {/* Sub-block 4 */}
              <div className="space-y-4">
                <h3 className="font-display font-bold text-xl text-slate-900 uppercase tracking-wide flex items-center gap-3">
                  <i className="fa-solid fa-diagram-project text-brand-primary text-lg" aria-hidden="true" />
                  Samlet styring skaber et mere overskueligt projekt
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Ved større opgaver kan vi varetage hovedentreprise eller totalentreprise. Det betyder, at vi kan stå for planlægning, kvalitetssikring og koordinering af de relevante faggrupper. Du får dermed en mere samlet proces, hvor tidsplan, håndværkere og praktiske beslutninger bliver håndteret med et tydeligt ansvar.
                </p>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Den samlede løsning er relevant ved blandt andet <Link to="/ydelser/tilbygning" className="text-brand-primary font-semibold hover:underline">nybyggeri, tilbygninger</Link>, omfattende boligrenovering og <Link to="/ydelser/ombygning" className="text-brand-primary font-semibold hover:underline">renovering af butikker eller kontorer</Link>. Vi tager udgangspunkt i projektets omfang og aftaler på forhånd, hvilke dele vi skal udføre, styre og koordinere for dig.
                </p>
              </div>

              {/* Sub-block 5 */}
              <div className="bg-slate-900 text-white p-6 sm:p-8 rounded-none space-y-4">
                <h3 className="font-display font-bold text-xl text-white uppercase tracking-wide flex items-center gap-3">
                  <i className="fa-solid fa-location-dot text-brand-primary text-lg" aria-hidden="true" />
                  Din lokale tømrer i Balle og Grenaa
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Søger du en tømrer i Balle eller en tømrer i Grenaa, er vi lokalt placeret på Djursland. Vi kender området og arbejder også i Tirstrup, Ebeltoft og de omkringliggende byer. Den lokale tilstedeværelse gør det nemt at tage en indledende snak om din opgave og gennemgå mulighederne på stedet.
                </p>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  <Link to="/kontakt" className="text-white font-semibold underline decoration-brand-primary hover:text-brand-primary transition-colors">Kontakt Tømrerfirmaet RNS</Link>, hvis du har brug for rådgivning om <Link to="/ydelser/toemrer" className="text-brand-primary font-semibold hover:underline">tømrerarbejde</Link>, snedkerarbejde, <Link to="/ydelser/maler" className="text-brand-primary font-semibold hover:underline">malerarbejde</Link> eller en større entreprise. Fortæl os, hvad du ønsker hjælp til, så ser vi nærmere på opgaven og udarbejder et uforpligtende tilbud med udgangspunkt i din bygning og dine behov.
                </p>
                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs sm:text-sm font-bold uppercase tracking-wider px-5 py-3 transition-colors shadow"
                  >
                    <span>Få et uforpligtende tilbud</span>
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar / Quick Links to all Services */}
            <div className="lg:col-span-5 space-y-8 sticky top-28">
              <div className="bg-slate-50 border border-slate-200/80 p-6 sm:p-8">
                <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-200">
                  <div>
                    <span className="text-xs font-bold text-brand-primary uppercase tracking-wider block">Oversigt</span>
                    <h3 className="font-display font-extrabold text-xl text-slate-900 uppercase tracking-wide">
                      Vi tilbyder
                    </h3>
                  </div>
                  <Link
                    to="/ydelser"
                    className="text-xs font-bold text-slate-600 hover:text-brand-primary flex items-center gap-1 transition-colors"
                  >
                    <span>Se alle</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>

                <p className="text-xs sm:text-sm text-slate-600 mb-6 leading-relaxed">
                  Gå direkte til vores specialiserede ydelser og læs mere om, hvad vi kan gøre for dit projekt:
                </p>

                <div className="grid grid-cols-1 gap-2.5">
                  {[
                    { slug: 'toemrer', name: 'Tømrerarbejde', icon: 'fa-solid fa-hammer', desc: 'Gulve, lofter, vægge, hegn og nybyg' },
                    { slug: 'udskiftning-af-vinduer', name: 'Udskiftning af vinduer og døre', icon: 'fa-solid fa-table-cells-large', desc: 'Mindre træk, bedre isolering og flot finish' },
                    { slug: 'sommerhusrenovering', name: 'Sommerhusrenovering', icon: 'fa-solid fa-house-chimney-window', desc: 'Kærlig hånd, terrasser og modernisering' },
                    { slug: 'asbestarbejde-og-tagrenovering', name: 'Nyt tag & asbestarbejde', icon: 'fa-solid fa-shield-halved', desc: 'Komplet tagudskiftning og sikker asbesthåndtering' },
                    { slug: 'tagrenovering', name: 'Tagrenovering', icon: 'fa-solid fa-house-chimney-crack', desc: 'Reparation, isolering og vedligeholdelse' },
                    { slug: 'tilbygning', name: 'Tilbygning & nybyg', icon: 'fa-solid fa-trowel-bricks', desc: 'Få mere plads med en skræddersyet tilbygning' },
                    { slug: 'ombygning', name: 'Ombygning & butiksrenovering', icon: 'fa-solid fa-store', desc: 'Modernisering af bolig, butik og kontor' },
                    { slug: 'maler', name: 'Maler på Djursland', icon: 'fa-solid fa-paint-roller', desc: 'Indendørs og udendørs malerarbejde' },
                  ].map((serviceItem) => (
                    <Link
                      key={serviceItem.slug}
                      to={`/ydelser/${serviceItem.slug}`}
                      className="group flex items-start gap-3 p-3.5 bg-white border border-slate-200/70 hover:border-brand-primary/60 hover:bg-amber-50/40 transition-all"
                    >
                      <div className="w-8 h-8 rounded-none bg-slate-100 group-hover:bg-brand-primary group-hover:text-white text-slate-700 flex items-center justify-center text-sm shrink-0 transition-colors mt-0.5">
                        <i className={serviceItem.icon} aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-display font-bold text-sm text-slate-900 group-hover:text-brand-primary transition-colors truncate">
                            {serviceItem.name}
                          </h4>
                          <i className="fa-solid fa-chevron-right text-[10px] text-slate-400 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all ml-1 shrink-0" aria-hidden="true" />
                        </div>
                        <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                          {serviceItem.desc}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Direct action card */}
                <div className="mt-6 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Har du spørgsmål?</p>
                    <a href="tel:28929768" className="text-slate-900 font-display font-bold text-base hover:text-brand-primary transition-colors flex items-center gap-1.5 mt-0.5">
                      <i className="fa-solid fa-phone text-xs text-brand-primary" aria-hidden="true" />
                      28 92 97 68
                    </a>
                  </div>
                  <Link
                    to="/kontakt"
                    className="w-full sm:w-auto text-center px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                  >
                    Kontakt os
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-slate-950 pt-20 pb-0 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4 max-w-2xl">
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white uppercase tracking-wider">
                Hvad kan vi hjælpe med som maler og tømrer?
              </h2>
              <p className="text-white leading-relaxed text-sm md:text-base">
                Som veletableret maler og tømrer nær Djursland står vi til rådighed ved enhver tømreropgave. Vores arbejde tager altid udgangspunkt i dit behov, og vi skræddersyr hver enkelt opgave efter dine ønsker.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-3 px-6 rounded-none transition-all inline-flex items-center gap-2 shrink-0 shadow-lg"
            >
              Kontakt os
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>
        </div>

        {/* Full-bleed panels container */}
        <div className="relative w-full h-auto lg:h-[60vh] overflow-hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {services.slice(0, 4).map((service, idx) => (
            <Link
              key={service.id}
              to={`/ydelser/${service.slug.da || service.slug}`}
              className="relative h-[40vh] md:h-[50vh] lg:h-full overflow-hidden group"
            >
              {service.hero_billede && service.hero_billede[0] && (
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${service.hero_billede[0].replace('https://assets.auxodev.dk/', '')}`}
                  alt={`${service.titel?.da || service.titel} - Tømrerfirmaet RNS på Djursland`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              )}
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/20 group-hover:via-slate-950/30 transition-all duration-500" />

              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-10 z-10">
                {/* Middle: Vertically rotated heading */}
                <div className="relative flex items-center justify-center flex-1">
                  <div className="lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:-rotate-90 lg:w-[400px] text-center">
                    <h3 className="font-display font-black text-white uppercase tracking-widest text-lg sm:text-xl md:text-2xl lg:text-2xl text-center leading-tight">
                      {service.titel.da.includes('&') ? (
                        <>
                          {service.titel.da.split('&')[0].trim()} &
                          <br />
                          {service.titel.da.split('&')[1].trim()}
                        </>
                      ) : (
                        service.titel.da
                      )}
                    </h3>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 uppercase tracking-wider">
              Hvad siger vores kunder?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Kundetilfredshed er vores absolut højeste prioritet. Her er et udpluk af de anmeldelser, vi har modtaget fra glade sommerhus- og boligejere.
            </p>
          </div>

          <ReviewSlider />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-50" id="kontakt-sektion">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-6">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 uppercase tracking-wider">
              Skal vi hjælpe med dit næste projekt?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Har du spørgsmål til vores arbejde – eller vil du have et uforpligtende tilbud på din næste tømrer- eller maleropgave – ser vi frem til din henvendelse.
            </p>

            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-none bg-brand-primary flex items-center justify-center text-white shrink-0">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <a href="tel:22439147" className="text-black hover:underline font-bold text-lg whitespace-nowrap">
                    +45 22 43 91 47
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-none bg-brand-primary flex items-center justify-center text-white shrink-0">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <a href="mailto:trns@mail.dk" className="text-black hover:underline font-bold text-lg whitespace-nowrap">
                    trns@mail.dk
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-none bg-brand-primary flex items-center justify-center text-white shrink-0">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div>
                  <p className="text-slate-600 text-sm">
                    Glatvedvej 28, 8444 Balle
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="space-y-4 max-w-2xl">
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 uppercase tracking-wider">
                Udvalgte projekter
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Her kan du se et udvalg af vores seneste projekter, som vi har udført for glade og tilfredse kunder på Djursland og i omegn.
              </p>
            </div>
            <Link
              to="/referencer"
              className="text-black hover:text-black/80 font-bold text-sm flex items-center gap-2 shrink-0"
            >
              Se alle projekter
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-none overflow-hidden border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  {project.billeder && project.billeder[0] && (
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${project.billeder[0].replace('https://assets.auxodev.dk/', '')}`}
                      alt={`${project.titel?.da || project.titel} - Håndværk og byggeri udført af Tømrerfirmaet RNS`}
                      className="w-full h-56 object-cover"
                    />
                  )}
                  <div className="p-6 space-y-3">
                    <h3 className="font-display font-bold text-lg text-slate-900 uppercase tracking-wider">
                      {project.titel.da}
                    </h3>
                    <div
                      className="text-sm text-slate-600 leading-relaxed line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: project.beskrivelse.da }}
                    />
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to="/referencer"
                    className="text-slate-900 hover:text-slate-700 font-bold text-sm flex items-center gap-1.5"
                  >
                    Se projektet i galleriet
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
